import React, { useCallback, useMemo, useState } from "react";
import useSWR from "swr";
import { fetcherWithOptions } from "../../util/request";
import { Spin, Table, Select, Space, Button, Typography } from "antd";
import Application from "../../types/admin/Application";
import { FRONTEND_BASEURL } from "../../constants";
import { LinkOutlined } from "@ant-design/icons";
import { ColumnsType, ColumnType } from "antd/es/table";
import _ from "lodash";

// Type definitions
interface SimplifiedApplication extends Record<string, any> {
  id: string;
  timestamp: string;
  preferredName?: string;
  firstName?: string;
  surname?: string;
  stageOfProcessing?: string;
  pic?: string;
  groupNumber?: string;
  gender?: string; // Changed from React.ReactNode to string
  firstUniChoice?: string;
  firstSubjectChoice?: string;
  secondUniChoice?: string;
  secondSubjectChoice?: string;
  familyIncome?: number;
  householdHeadcount?: number;
  perCapitaIncome?: number;
  firstGen?: string;
  familyProperties?: string;
  qualifications?: string;
  school?: string;
  academicPotential?: string | number;
  financialBackground?: string | number;
  jardinePotential?: string | number;
  ppPotential?: string | number;
  hksesPotential?: string | number;
}

type ColumnName = keyof SimplifiedApplication;

// Helper Components
const ApplicationLink: React.FC<{ id: string }> = ({ id }) => (
  <a
    href={`${FRONTEND_BASEURL}/admin/application/${id}`}
    target="_blank"
    rel="noopener noreferrer"
  >
    <LinkOutlined style={{ fontSize: "1.5rem" }} />
  </a>
);

const IncomeCell: React.FC<{ value?: number }> = ({ value }) => {
  const color = useIncomeColor(value);

  return (
    <span style={{ color, fontWeight: "bold" }}>
      {value || value === 0 ? value.toLocaleString() : "N/A"}
    </span>
  );
};

// Custom Hooks
const useIncomeColor = (income?: number): string => {
  if (!income && income !== 0) return "inherit";
  const maxIncome = 50000;
  const clampedIncome = Math.min(Math.max(income, 0), maxIncome);
  const percentage = clampedIncome / maxIncome;
  const red = Math.min(Math.round(255 * percentage * 1.3), 255);
  const green = Math.min(Math.round(255 * (1 - percentage) * 1.3), 255);
  return `rgb(${red}, ${green}, 0)`;
};


const useProcessedApplicationData = (applications?: Application[]): SimplifiedApplication[] => {
  return useMemo(() => {
    if (!applications) return [];

    return applications.map((application): SimplifiedApplication => {
      const familyIncome = application.savedForm.familyIncome as number | undefined;
      const householdHeadcount = application.savedForm.householdHeadcount as number | undefined;
      const perCapitaIncome =
        familyIncome && householdHeadcount
          ? Math.round(familyIncome / householdHeadcount)
          : undefined;
      const stageOfProcessing = application.processingData.status as string | undefined;
      const pic = application.processingData.personInCharge as string | undefined;
      const groupNumber = application.processingData.groupNumber as string | undefined;
      const qualifications = Array.isArray(application.savedForm.qualifications)
        ? application.savedForm.qualifications.join("; ")
        : (application.savedForm.qualifications as string | undefined);
      const gender = application.savedForm.gender as string | undefined; // Store the raw string value
      const academicPotential = application.processingData.academicPotential as string | undefined;
      const financialBackground = application.processingData.financialBackground as string | undefined;
      const jardinePotential = application.processingData.jardinePotential as string | undefined;
      const ppPotential = application.processingData.ppPotential as string | undefined;
      const hksesPotential = application.processingData.hksesPotential as string | undefined;

      const res: SimplifiedApplication = {
        ...application.savedForm,
        qualifications,
        timestamp: application.submissionTimestamp
          ? new Date(application.submissionTimestamp).toLocaleString("en-GB", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          })
          : "N/A",
        id: application.id,
        perCapitaIncome,
        stageOfProcessing: stageOfProcessing || "Pending",
        pic,
        groupNumber,
        gender,
        academicPotential,
        financialBackground,
        jardinePotential,
        ppPotential,
        hksesPotential,
      };

      // Change undefined values to N/A
      return Object.entries(res).reduce((acc, [key, value]) => {
        if (value === undefined) {
          acc[key] = "N/A";
        } else {
          acc[key] = value;
        }
        return acc;
      }, {} as SimplifiedApplication);
    });
  }, [applications]);
};

// Column Selection Component
interface ColumnSelectorProps {
  allColumnNames: ColumnName[];
  visibleColumns: ColumnName[];
  setVisibleColumns: (columns: ColumnName[]) => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  allColumnNames,
  visibleColumns,
  setVisibleColumns,
}) => {
  const columnOptions = useMemo(
    () =>
      allColumnNames.map((column) => ({
        label:
          column === "gender"
            ? "Sex"
            : column === "familyProperties"
              ? "Ownership of Properties"
              : column === "pic"
                ? "PIC"
                : _.startCase(column as string),
        value: column,
      })),
    [allColumnNames]
  );

  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <Space size="middle" align="center">
        <Typography.Text strong>Column Selector:</Typography.Text>
        <Select
          mode="multiple"
          placeholder="Select columns to display"
          value={visibleColumns as string[]}
          onChange={(values) => setVisibleColumns(values as ColumnName[])}
          style={{ minWidth: 300, width: "auto" }}
          options={columnOptions}
          allowClear
          showSearch
        />
        <Button
          onClick={() => setVisibleColumns([...allColumnNames])}
          disabled={visibleColumns.length === allColumnNames.length}
        >
          Select All
        </Button>
        <Button
          onClick={() => setVisibleColumns(["timestamp"])}
          disabled={visibleColumns.length === 1 && visibleColumns[0] === "timestamp"}
        >
          Reset
        </Button>
      </Space>
    </div>
  );
};

// Main Component
const ApplicationsDataTable2025: React.FC = () => {
  // Define all available columns
  const allColumnNames = useMemo<ColumnName[]>(
    () => [
      "timestamp",
      "preferredName",
      "firstName",
      "surname",
      "stageOfProcessing",
      "pic",
      "school",
      "groupNumber",
      "firstUniChoice",
      "firstSubjectChoice",
      "familyIncome",
      "householdHeadcount",
      "perCapitaIncome",
      "firstGen",
      "familyProperties",
      "qualifications",
      "academicPotential",
      "financialBackground",
      "jardinePotential",
      "ppPotential",
      "hksesPotential",
      "gender",
    ],
    []
  );

  // State for visible columns - initially set to all columns
  const [visibleColumns, setVisibleColumns] = useState<ColumnName[]>(allColumnNames);

  // Fetch data with SWR
  const {
    data: applications,
    error,
    isLoading,
  } = useSWR<Application[]>(
    [
      `/admin/application/list`,
      { params: { page: 1, pageSize: 10000, unsubmitted: false } },
    ],
    fetcherWithOptions
  );

  // Process applications data for the table
  const tableData = useProcessedApplicationData(applications);

  // Format column header helper
  const formatColumnName = useCallback((name: ColumnName): string => {
    if (name === "gender") return "Sex";
    if (name === "familyProperties") return "Ownership of Properties";
    if (name === "pic") return "PIC";
    return _.startCase(name as string);
  }, []);

  // Generate column filters
  const createFilterValues = useCallback((name: ColumnName, data: SimplifiedApplication[]) => {
    return data
      .map((record) => {
        const value = record[name];
        return value ? String(value) : null;
      })
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index)
      .map((value) => ({ text: value, value }));
  }, []);

  // Memoized columns configuration
  const columns = useMemo<ColumnsType<SimplifiedApplication>>(() => {
    if (!tableData.length) return [];

    // Create columns based on visible columns selection
    const columnDefs = visibleColumns.map((name) => {
      // Special rendering for per capita income
      if (name === "perCapitaIncome") {
        return {
          title: formatColumnName(name),
          dataIndex: name,
          key: name,
          render: (value: number | undefined) => <IncomeCell value={value} />,
          sorter: (a, b) => (Number(a[name]) || 0) - (Number(b[name]) || 0),
        } as ColumnType<SimplifiedApplication>;
      }

      // Generate filter values
      const filterValues = createFilterValues(name, tableData);

      // Return standard column configuration
      return {
        title: formatColumnName(name),
        dataIndex: name,
        key: name,
        sorter: (a, b) => {
          if (typeof a[name] === "string" && typeof b[name] === "string") {
            return a[name].localeCompare(b[name]);
          }
          if (!isNaN(Number(a[name])) && !isNaN(Number(b[name]))) {
            return Number(a[name]) - Number(b[name]);
          }
          return String(a[name] || "").localeCompare(String(b[name] || ""));
        },
        filters: filterValues.length > 0 ? filterValues : undefined,
        onFilter: (value, record) => {
          if (!record[name]) return false;
          return String(record[name]).includes(String(value));
        },
        filterSearch: true,
      } as ColumnType<SimplifiedApplication>;
    });

    // Add link column
    const linkColumn: ColumnType<SimplifiedApplication> = {
      title: "Link",
      dataIndex: "link",
      key: "link",
      fixed: "left",
      width: 70,
      render: (_, record) => <ApplicationLink id={record.id} />,
    };

    // Set default sort order for timestamp
    const timestampColumn = columnDefs.find((col) => col.key === "timestamp");
    if (timestampColumn) {
      // sort by timestamp not formatted string
      timestampColumn.sorter = (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      timestampColumn.defaultSortOrder = "descend";
    }

    return [linkColumn, ...columnDefs];
  }, [tableData, visibleColumns, formatColumnName, createFilterValues]);

  // Table component customization
  const components = useMemo(
    () => ({
      header: {
        cell: (props: any) => (
          <th {...props} style={{ ...props.style, padding: "8px 8px" }} />
        ),
      },
      body: {
        row: (props: any) => (
          <tr {...props} style={{ ...props.style, height: "36px" }} />
        ),
        cell: (props: any) => (
          <td {...props} style={{ ...props.style, padding: "4px 8px" }} />
        ),
      },
    }),
    []
  );

  // Loading and error states
  if (isLoading) return <Spin size="large" />;
  if (error) return <div>Failed to load application data</div>;
  if (!applications) return null;

  return (
    <>
      <ColumnSelector
        allColumnNames={allColumnNames}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
      />

      <Table
        components={components}
        dataSource={tableData}
        columns={columns}
        rowKey="id"
        pagination={{
          position: ["bottomRight"],
          pageSizeOptions: ["10", "20", "50", "100"],
          showSizeChanger: true,
        }}
        scroll={{ x: true }}
      />
    </>
  );
};

export default ApplicationsDataTable2025;
